import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { SERVER_URL } from "../config";
import SearchableDropdown from "../components/SearchableDropdown";
import Sidebar from "../components/Sidebar";

// ─── Palette ──────────────────────────────────────────────────────────────────
const PIE_COLORS = [
  "#6366f1","#8b5cf6","#a78bfa","#3b82f6","#06b6d4",
  "#10b981","#f59e0b","#ef4444","#ec4899","#14b8a6",
  "#f97316","#84cc16","#0ea5e9","#d946ef","#fb7185",
];
const BAR_OPEN    = "#3b82f6";
const BAR_SUCCESS = "#10b981";

// ─── FY helper ────────────────────────────────────────────────────────────────
function getCurrentFY(fyList = []) {
  const now = new Date();
  const m = now.getMonth() + 1;
  const y = now.getFullYear();
  const sy = m >= 4 ? y : y - 1;
  const local = `${sy}-${String(sy + 1).slice(2)}`;
  if (!fyList.length) return local;
  return fyList.includes(local) ? local : fyList[fyList.length - 1];
}

// ─── Parse FY string "2024-25" → { start: "2024-04-01", end: "2025-03-31" } ──
function getFYDateBounds(fy) {
  if (!fy || fy === "all") return { min: "", max: "" };
  const match = fy.match(/^(\d{4})-(\d{2})$/);
  if (!match) return { min: "", max: "" };
  const startYear = parseInt(match[1], 10);
  const endYear   = startYear + 1;
  return {
    min: `${startYear}-04-01`,
    max: `${endYear}-03-31`,
  };
}

// ─── Chart.js singleton loader ────────────────────────────────────────────────
let chartJsLoaded = false;
let chartJsCallbacks = [];
function loadChartJs(cb) {
  if (chartJsLoaded) { cb(); return; }
  chartJsCallbacks.push(cb);
  if (chartJsCallbacks.length > 1) return;
  const s = document.createElement("script");
  s.src = "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js";
  s.onload = () => {
    chartJsLoaded = true;
    chartJsCallbacks.forEach(fn => fn());
    chartJsCallbacks = [];
  };
  document.head.appendChild(s);
}

// ─── niceCeil ─────────────────────────────────────────────────────────────────
function niceCeil(v) {
  if (v === 0) return 10;
  const exp = Math.pow(10, Math.floor(Math.log10(v)));
  return Math.ceil(v / exp) * exp;
}

// ══════════════════════════════════════════════════════════════════════════════
//  ScrollableBarChart
// ══════════════════════════════════════════════════════════════════════════════
const ScrollableBarChart = ({ data, dataKey = "name", bars = [], height = 300, barWidthPx = 65 }) => {
  const Y_AXIS_WIDTH = 52;
  const yCanvasRef = useRef(null);
  const bCanvasRef = useRef(null);
  const yChartRef  = useRef(null);
  const bChartRef  = useRef(null);

  const labels  = data.map(d => d[dataKey]);
  const maxVal  = data.reduce((mx, row) => { bars.forEach(b => { mx = Math.max(mx, Number(row[b.key] || 0)); }); return mx; }, 0);
  const yMax    = niceCeil(maxVal);
  const scrollWidth = Math.max(labels.length * (barWidthPx * bars.length + 16) + 32, 300);

  useEffect(() => {
    loadChartJs(() => {
      const Chart = window.Chart;
      if (!Chart || !yCanvasRef.current || !bCanvasRef.current) return;
      [yChartRef, bChartRef].forEach(r => { if (r.current) { r.current.destroy(); r.current = null; } });

      const gridColor = "rgba(148,163,184,0.15)";
      const tickColor = "#64748b";

      yChartRef.current = new Chart(yCanvasRef.current, {
        type: "bar",
        data: { labels: ["_"], datasets: [{ data: [yMax], backgroundColor: "transparent", borderColor: "transparent" }] },
        options: {
          animation: false, responsive: false,
          plugins: { legend: { display: false }, tooltip: { enabled: false } },
          scales: {
            x: { display: false },
            y: {
              display: true, min: 0, max: yMax,
              grid: { color: gridColor, drawBorder: false },
              ticks: { color: tickColor, font: { size: 11, weight: "600" }, maxTicksLimit: 6 },
              border: { display: false },
            },
          },
          layout: { padding: { top: 8, bottom: 44, left: 0, right: 0 } },
        },
      });

      bChartRef.current = new Chart(bCanvasRef.current, {
        type: "bar",
        data: {
          labels,
          datasets: bars.map(b => ({
            label: b.label ?? b.key, data: data.map(d => Number(d[b.key] || 0)),
            backgroundColor: b.color, borderRadius: 5, barPercentage: 0.78, categoryPercentage: 0.78,
          })),
        },
        options: {
          animation: { duration: 400 }, responsive: false,
          plugins: {
            legend: {
              display: true, position: "top", align: "end",
              labels: { color: "#475569", font: { size: 11, weight: "600" }, boxWidth: 10, boxHeight: 10, borderRadius: 2, useBorderRadius: true, padding: 12 },
            },
            tooltip: {
              backgroundColor: "rgba(15,23,42,0.92)", titleColor: "#e2e8f0", bodyColor: "#cbd5e1",
              borderColor: "rgba(148,163,184,0.2)", borderWidth: 1, cornerRadius: 10, padding: 10,
            },
          },
          scales: {
            x: {
              display: true,
              ticks: { color: "#64748b", font: { size: 11, weight: "600" }, maxRotation: 40, minRotation: 40, autoSkip: false },
              grid: { display: false }, border: { display: false },
            },
            y: { display: false, min: 0, max: yMax },
          },
          layout: { padding: { top: 8, bottom: 0, left: 0, right: 8 } },
        },
      });
    });

    return () => { [yChartRef, bChartRef].forEach(r => { if (r.current) { r.current.destroy(); r.current = null; } }); };
  }, [JSON.stringify({ data, bars, yMax })]);

  return (
    <div className="flex w-full items-start overflow-hidden">
      <div className="shrink-0" style={{ width: Y_AXIS_WIDTH }}>
        <canvas ref={yCanvasRef} width={Y_AXIS_WIDTH} height={height} className="block" />
      </div>
      <div className="flex-1 overflow-x-auto overflow-y-hidden stats-scrollbar">
        <div style={{ width: scrollWidth, minWidth: "100%" }}>
          <canvas ref={bCanvasRef} width={scrollWidth} height={height} className="block" />
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  DoughnutChart
// ══════════════════════════════════════════════════════════════════════════════
const DoughnutChart = ({ data, height = 290 }) => {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  useEffect(() => {
    loadChartJs(() => {
      const Chart = window.Chart;
      if (!Chart || !canvasRef.current || !data.length) return;
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
      chartRef.current = new Chart(canvasRef.current, {
        type: "doughnut",
        data: {
          labels: data.map(d => d.name),
          datasets: [{ data: data.map(d => d.percentage), backgroundColor: PIE_COLORS, borderColor: "rgba(255,255,255,0.8)", borderWidth: 2, hoverOffset: 8 }],
        },
        options: {
          animation: { duration: 600 }, responsive: false, cutout: "48%",
          plugins: {
            legend: {
              display: true, position: "bottom",
              labels: { color: "#475569", font: { size: 11, weight: "600" }, boxWidth: 10, boxHeight: 10, borderRadius: 2, useBorderRadius: true, padding: 10 },
            },
            tooltip: {
              backgroundColor: "rgba(15,23,42,0.92)", titleColor: "#e2e8f0", bodyColor: "#6ee7b7",
              borderColor: "rgba(148,163,184,0.2)", borderWidth: 1, cornerRadius: 10, padding: 10,
              callbacks: { label: ctx => { const d = data[ctx.dataIndex]; return ` ${d.percentage.toFixed(1)}%  ₹${Number(d.amount || 0).toLocaleString("en-IN")}`; } },
            },
          },
        },
      });
    });
    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [JSON.stringify(data)]);

  return <div className="flex justify-center"><canvas ref={canvasRef} width={300} height={height} className="block" /></div>;
};

// ─── Shared UI pieces ─────────────────────────────────────────────────────────
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center gap-2" style={{ height: 180 }}>
    <svg width={40} height={40} fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0120 9.414V19a2 2 0 01-2 2z" />
    </svg>
    <p className="text-[13px] font-semibold text-slate-400">No data available</p>
  </div>
);

// ── White card — matches LeaveApplications card style ─────────────────────────
const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-xl bg-white border border-slate-200 p-6 w-full box-border ${className}`}
    style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}
  >
    {children}
  </div>
);

const pulseClass = "animate-pulse";
const SkBlock = ({ w = "100%", h = 16, mb = 0 }) => (
  <div className={`rounded-lg ${pulseClass} bg-slate-100`} style={{ width: w, height: h, marginBottom: mb }} />
);

const SkeletonCard = () => (
  <Card style={{ minHeight: 360 }}>
    <SkBlock w="45%" h={16} mb={8} /><SkBlock w="30%" h={11} mb={28} />
    <div className="flex items-end gap-2 px-4" style={{ height: 200 }}>
      {[55,80,40,90,60,70,45,65].map((h, i) => (
        <div key={i} className={`flex-1 rounded-t-[5px] ${pulseClass} bg-slate-100`} style={{ height: `${h}%` }} />
      ))}
    </div>
  </Card>
);

const SkeletonPie = () => (
  <Card>
    <div className="flex flex-col items-center justify-center gap-4" style={{ minHeight: 360 }}>
      <div className={`rounded-full ${pulseClass} bg-slate-100`} style={{ width: 180, height: 180 }} />
      <div className="flex gap-3">
        {[70, 55, 80].map((w, i) => <SkBlock key={i} w={w} h={11} />)}
      </div>
    </div>
  </Card>
);

const SkeletonTile = () => (
  <div className="rounded-xl p-[18px] border border-slate-100 bg-slate-50">
    <SkBlock w="60%" h={10} mb={10} /><SkBlock w="40%" h={28} mb={8} /><SkBlock w="50%" h={9} />
  </div>
);

const ErrorBanner = ({ message, onRetry }) => (
  <div className="rounded-xl px-5 py-3 flex items-center justify-between gap-3 bg-rose-50 border border-rose-200">
    <div className="flex items-center gap-2">
      <svg width={16} height={16} fill="none" viewBox="0 0 24 24" stroke="#f43f5e" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p className="text-[13px] font-semibold text-rose-600">{message}</p>
    </div>
    <button
      onClick={onRetry}
      className="rounded-lg px-[14px] py-[6px] text-xs font-bold border-0 cursor-pointer bg-rose-100 text-rose-600 hover:bg-rose-200 transition-colors"
    >
      Retry
    </button>
  </div>
);

// ── Stat tile — matches LeaveApplications modal stat tile style ────────────────
const TILE_THEMES = {
  indigo:  { bg: "bg-blue-50 border border-blue-100",     accent: "text-blue-500",    val: "text-blue-800"    },
  emerald: { bg: "bg-emerald-50 border border-emerald-100", accent: "text-emerald-500", val: "text-emerald-800" },
  sky:     { bg: "bg-sky-50 border border-sky-100",       accent: "text-sky-500",     val: "text-sky-800"     },
  violet:  { bg: "bg-violet-50 border border-violet-100", accent: "text-violet-500",  val: "text-violet-800"  },
  teal:    { bg: "bg-teal-50 border border-teal-100",     accent: "text-teal-500",    val: "text-teal-800"    },
  rose:    { bg: "bg-rose-50 border border-rose-100",     accent: "text-rose-500",    val: "text-rose-800"    },
};

const StatTile = ({ label, value, sub, color = "indigo", icon }) => {
  const t = TILE_THEMES[color] || TILE_THEMES.indigo;
  return (
    <div className={`rounded-xl px-4 py-4 ${t.bg}`}>
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-bold uppercase mb-[6px] tracking-widest ${t.accent}`}>{label}</p>
          <p className={`text-[26px] font-extrabold m-0 mb-1 ${t.val}`}>{value}</p>
          {sub && <p className={`text-[11px] opacity-80 ${t.accent}`}>{sub}</p>}
        </div>
        {icon && <span className="text-[22px] ml-2">{icon}</span>}
      </div>
    </div>
  );
};

// ── Chart card header ──────────────────────────────────────────────────────────
const CardHeader = ({ title, sub }) => (
  <div className="mb-4 flex items-center gap-2">
    <div className="w-1 h-5 rounded-full" style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
    <div>
      <h3 className="text-[14px] font-bold m-0 text-slate-800">{title}</h3>
      {sub && <p className="text-[11px] mt-0.5 text-slate-400">{sub}</p>}
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
//  CHART COMPONENTS
// ══════════════════════════════════════════════════════════════════════════════

const LeadSummaryCards = ({ summary = {}, loading }) => {
  if (loading) return (
    <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
      {Array.from({ length: 6 }).map((_, i) => <SkeletonTile key={i} />)}
    </div>
  );
  const fmt  = n => Number(n || 0).toLocaleString("en-IN");
  const fmtL = n => { const v = Number(n || 0); return v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : `₹${fmt(v)}`; };
  const lostLeads = Number(summary.total_leads || 0) - Number(summary.successful_leads || 0) - Number(summary.open_leads || 0);
  const tiles = [
    { label: "Total Leads",      value: fmt(summary.total_leads),         sub: "All assigned leads",           color: "indigo",  icon: "📋" },
    { label: "Leads Successful", value: fmt(summary.successful_leads),    sub: `${summary.successful_rate||0}% rate`,           color: "emerald", icon: "🏆" },
    { label: "Leads Open",       value: fmt(summary.open_leads),          sub: `${summary.open_rate||0}% in pipeline`,          color: "sky",     icon: "🔄" },
    { label: "Lost Leads",       value: fmt(lostLeads),                   sub: "Unconverted leads",             color: "rose",    icon: "❌" },
    { label: "Avg Conversion",   value: `${summary.avg_conversion||0}%`,  sub: "Successful only",               color: "violet",  icon: "📈" },
    { label: "Total Revenue",    value: fmtL(summary.total_revenue),      sub: "Successful leads only",         color: "teal",    icon: "💰" },
  ];
  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
      {tiles.map(t => <StatTile key={t.label} {...t} />)}
    </div>
  );
};

const RevenuePieChart = ({ branchRevenue = [] }) => {
  const normalized = branchRevenue.reduce((acc, b) => {
    const key = (b.branch || "Unknown").trim().toUpperCase();
    const ex  = acc.find(a => a.branch === key);
    if (ex) ex.amount += parseFloat(b.amount) || 0;
    else acc.push({ branch: key, amount: parseFloat(b.amount) || 0 });
    return acc;
  }, []);
  const total = normalized.reduce((s, b) => s + b.amount, 0);
  const data  = normalized.filter(b => b.amount > 0).map(b => ({
    name: b.branch,
    percentage: total > 0 ? parseFloat((b.amount * 100 / total).toFixed(2)) : 0,
    amount: b.amount,
  }));
  return (
    <Card>
      <CardHeader title="Revenue by Branch" sub="% share · Successful leads only" />
      {data.length === 0 ? <EmptyState /> : <DoughnutChart data={data} height={290} />}
    </Card>
  );
};

const ProductBranchBarChart = ({ productLeads = [] }) => {
  const [sel, setSel] = useState("all");
  const products = ["all", ...new Set(productLeads.map(d => d.product).filter(Boolean))];
  useEffect(() => { if (sel !== "all" && !products.includes(sel)) setSel("all"); }, [JSON.stringify(products)]);
  const productOptions = products.map(p => ({ value: String(p), label: p === "all" ? "All Products" : String(p) }));
  const chartData = productLeads
    .filter(d => sel === "all" || d.product === sel)
    .reduce((acc, row) => {
      const bKey = (row.branch || "Unknown").trim().toUpperCase();
      const ex   = acc.find(a => a.branch === bKey);
      if (ex) { ex.Open += Number(row.open)||0; ex.Successful += Number(row.successful)||0; }
      else acc.push({ branch: bKey, Open: Number(row.open)||0, Successful: Number(row.successful)||0 });
      return acc;
    }, [])
    .sort((a, b) => a.branch.localeCompare(b.branch));
  return (
    <Card>
      <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
        <CardHeader title="Product Leads by Branch" sub="Scroll right →" />
        <div style={{ width: 196 }}>
          <SearchableDropdown label="Filter by Product" options={productOptions} value={sel} onChange={v => setSel(v || "all")} placeholder="All Products" />
        </div>
      </div>
      {chartData.length === 0 ? <EmptyState /> : (
        <ScrollableBarChart data={chartData} dataKey="branch"
          bars={[{ key: "Open", label: "Open", color: BAR_OPEN }, { key: "Successful", label: "Successful", color: BAR_SUCCESS }]}
          height={280} barWidthPx={55} />
      )}
    </Card>
  );
};

const OverallProductBarChart = ({ productLeads = [] }) => {
  const allBranches = [...new Set(productLeads.map(d => (d.branch || "").trim().toUpperCase()).filter(Boolean))].sort();
  const [selBranch, setSelBranch] = useState("all");
  useEffect(() => { if (selBranch !== "all" && !allBranches.includes(selBranch)) setSelBranch("all"); }, [JSON.stringify(allBranches)]);
  const branchOptions = [{ value: "all", label: "All Branches" }, ...allBranches.map(b => ({ value: b, label: b }))];
  const chartData = productLeads
    .filter(d => selBranch === "all" || (d.branch || "").trim().toUpperCase() === selBranch)
    .reduce((acc, row) => {
      const pKey = (row.product || "Unknown").trim();
      const ex   = acc.find(a => a.product === pKey);
      if (ex) { ex.Open += Number(row.open)||0; ex.Successful += Number(row.successful)||0; }
      else acc.push({ product: pKey, Open: Number(row.open)||0, Successful: Number(row.successful)||0 });
      return acc;
    }, [])
    .filter(d => d.Open > 0 || d.Successful > 0)
    .sort((a, b) => (b.Open + b.Successful) - (a.Open + a.Successful));
  return (
    <Card>
      <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
        <CardHeader title="Leads by Product" sub="Scroll right →" />
        <div style={{ width: 196 }}>
          <SearchableDropdown label="Filter by Branch" options={branchOptions} value={selBranch} onChange={v => setSelBranch(v || "all")} placeholder="All Branches" />
        </div>
      </div>
      {chartData.length === 0 ? <EmptyState /> : (
        <ScrollableBarChart data={chartData} dataKey="product"
          bars={[{ key: "Open", label: "Open", color: BAR_OPEN }, { key: "Successful", label: "Successful", color: BAR_SUCCESS }]}
          height={280} barWidthPx={60} />
      )}
    </Card>
  );
};

const EmployeeBranchBarChart = ({ employeeLeads = [], branches = [] }) => {
  const normalizedBranches = [...new Set(branches.map(b => (b || "").trim().toUpperCase()))].filter(Boolean);
  const [selBranch, setSelBranch] = useState("all");
  useEffect(() => { if (selBranch !== "all" && !normalizedBranches.includes(selBranch)) setSelBranch("all"); }, [JSON.stringify(normalizedBranches)]);
  const branchOptions = [{ value: "all", label: "All Branches" }, ...normalizedBranches.map(b => ({ value: b, label: b }))];
  const chartData = employeeLeads
    .filter(d => selBranch === "all" || (d.branch || "").trim().toUpperCase() === selBranch)
    .map(d => ({ name: d.employee_name, Open: Number(d.open)||0, Successful: Number(d.successful)||0 }))
    .filter(d => d.Open > 0 || d.Successful > 0);
  return (
    <Card>
      <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
        <CardHeader title="Employee Lead Performance" sub="Scroll right →" />
        <div style={{ width: 196 }}>
          <SearchableDropdown label="Filter by Branch" options={branchOptions} value={selBranch} onChange={v => setSelBranch(v || "all")} placeholder="All Branches" />
        </div>
      </div>
      {chartData.length === 0 ? <EmptyState /> : (
        <ScrollableBarChart data={chartData} dataKey="name"
          bars={[{ key: "Open", label: "Open", color: BAR_OPEN }, { key: "Successful", label: "Successful", color: BAR_SUCCESS }]}
          height={300} barWidthPx={55} />
      )}
    </Card>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  STICKY HEADER — LeaveApplications style
// ══════════════════════════════════════════════════════════════════════════════
const StatsHeader = ({ filters, setFilters, onRefresh, loading, filterOptions, onOpenSidebar }) => {
  const { financialYears = [], branches = [], teams = [], employees = [] } = filterOptions;

  const fyOptions       = [{ value: "all", label: "All Years" }, ...financialYears.map(fy => ({ value: String(fy), label: `FY ${fy}` }))];
  const branchOptions   = [{ value: "all", label: "All Branches" }, ...branches.map(b => ({ value: String(b.name ?? b), label: String(b.name ?? b) }))];
  const teamOptions     = [{ value: "all", label: "All Teams" }, ...teams.map(t => ({ value: String(t.id ?? t), label: String(t.name ?? t) }))];
  const employeeOptions = [{ value: "all", label: "All Employees" }, ...employees.map(e => ({ value: String(e.user_id ?? ""), label: String(e.employee_name ?? e.user_id ?? "") }))];

  // ── FY-constrained date bounds ──────────────────────────────────────────────
  const { min: fyMin, max: fyMax } = getFYDateBounds(filters.financialYear);

  const handleDateFrom = (val) => {
    // Clamp within FY bounds
    let clamped = val;
    if (fyMin && val < fyMin) clamped = fyMin;
    if (fyMax && val > fyMax) clamped = fyMax;
    setFilters(f => ({
      ...f,
      dateFrom: clamped,
      // If dateTo is now before new dateFrom, reset it
      dateTo: f.dateTo && clamped && f.dateTo < clamped ? "" : f.dateTo,
    }));
  };

  const handleDateTo = (val) => {
    let clamped = val;
    if (fyMin && val < fyMin) clamped = fyMin;
    if (fyMax && val > fyMax) clamped = fyMax;
    setFilters(f => ({
      ...f,
      dateTo: clamped,
      // If dateFrom is now after new dateTo, reset it
      dateFrom: f.dateFrom && clamped && f.dateFrom > clamped ? "" : f.dateFrom,
    }));
  };

  return (
    <div
      className="sticky top-0 z-20 border-b border-slate-200"
      style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}
    >
      {/* Top accent line — same gradient as LeaveApplications */}
      <div className="h-0.5 w-full" style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }} />

      <div className="px-7 py-3.5">
        {/* Title row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4">
            <button
              onClick={onOpenSidebar}
              className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0 cursor-pointer"
            >
              <Bars3Icon className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2.5">
              <div className="w-1 h-6 rounded-full" style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
              <h1 className="text-lg font-bold m-0 tracking-tight text-slate-800">
                Company Performance Analytics
              </h1>
            </div>
          </div>
          <button
            onClick={onRefresh}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-lg text-xs font-semibold border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600 transition-colors cursor-pointer"
            style={{ padding: "8px 16px", opacity: loading ? 0.5 : 1, cursor: loading ? "not-allowed" : "pointer" }}
          >
            <svg style={{ width: 13, height: 13, animation: loading ? "spin 1s linear infinite" : "none" }}
              fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {/* Filter row */}
        <div className="flex gap-3 flex-wrap items-end">
          {/* Dropdown filters */}
          {[
            { label: "Financial Year", opts: fyOptions,       key: "financialYear", onChange: v => setFilters(f => ({ ...f, financialYear: v || "all", dateFrom: "", dateTo: "" })) },
            { label: "Branch",         opts: branchOptions,   key: "branch",        onChange: v => setFilters(f => ({ ...f, branch: v || "all" })) },
            { label: "Team",           opts: teamOptions,     key: "team",          onChange: v => setFilters(f => ({ ...f, team: v || "all" })) },
            { label: "Employee",       opts: employeeOptions, key: "employee",      onChange: v => setFilters(f => ({ ...f, employee: v || "all" })) },
          ].map(({ label, opts, key, onChange }) => (
            <div key={key} className="shrink-0" style={{ minWidth: 160 }}>
              <SearchableDropdown
                label={label} options={opts}
                value={filters[key] || "all"}
                onChange={onChange}
                placeholder={`All ${label}s`}
                disabled={loading}
              />
            </div>
          ))}

          {/* FY-constrained date pickers */}
          {[
            { label: "Date From", key: "dateFrom", handler: handleDateFrom, minBound: fyMin, maxBound: filters.dateTo || fyMax },
            { label: "Date To",   key: "dateTo",   handler: handleDateTo,   minBound: filters.dateFrom || fyMin, maxBound: fyMax },
          ].map(({ label, key, handler, minBound, maxBound }) => (
            <div key={key} className="flex flex-col gap-1 shrink-0">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</label>
              <input
                type="date"
                value={filters[key]}
                min={minBound || undefined}
                max={maxBound || undefined}
                onChange={e => handler(e.target.value)}
                disabled={loading || (!fyMin && filters.financialYear === "all")}
                className="rounded-lg text-[12px] outline-none cursor-pointer border border-slate-200 bg-slate-50 text-slate-700 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                style={{ padding: "7px 10px" }}
                title={fyMin ? `FY range: ${fyMin} → ${fyMax}` : "Select a Financial Year first"}
              />
              {/* {fyMin && (
                <span className="text-[9px] text-slate-400 font-medium">
                  {fyMin.slice(0,7)} → {fyMax.slice(0,7)}
                </span>
              )} */}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  PAGE
// ══════════════════════════════════════════════════════════════════════════════
const CompanyStatisticsPage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ financialYears: [], branches: [], teams: [], employees: [] });
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
  const [filters, setFilters] = useState({ financialYear: "", branch: "all", team: "all", employee: "all", dateFrom: "", dateTo: "" });
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [summary,       setSummary]       = useState({});
  const [branchRevenue, setBranchRevenue] = useState([]);
  const [productLeads,  setProductLeads]  = useState([]);
  const [employeeLeads, setEmployeeLeads] = useState([]);

  useEffect(() => {
    setFilterOptionsLoading(true);
    axios.get(`${SERVER_URL}/Rahul/filter_options.php`)
      .then(res => {
        const d = res.data;
        const fys = d.financialYears || [];
        setFilterOptions({ financialYears: fys, branches: d.branches||[], teams: d.teams||[], employees: d.employees||[] });
        setFilters(f => ({ ...f, financialYear: getCurrentFY(fys) }));
      })
      .catch(() => setFilters(f => ({ ...f, financialYear: getCurrentFY([]) })))
      .finally(() => setFilterOptionsLoading(false));
  }, []);

  useEffect(() => {
    if (!filters.financialYear || filters.financialYear === "all") return;
    axios.get(`${SERVER_URL}/Rahul/filter_options.php`, { params: { financial_year: filters.financialYear } })
      .then(res => { setFilterOptions(prev => ({ ...prev, employees: res.data.employees || [] })); setFilters(f => ({ ...f, employee: "all" })); })
      .catch(() => {});
  }, [filters.financialYear]);

  const fetchData = useCallback(() => {
    if (!filters.financialYear) return;
    setLoading(true); setError(null);
    axios.get(`${SERVER_URL}/Rahul/company_statistics.php`, {
      params: { financial_year: filters.financialYear, branch: filters.branch, team: filters.team, employee: filters.employee, date_from: filters.dateFrom, date_to: filters.dateTo },
    })
      .then(res => {
        const d = res.data;
        setSummary(d.summary || {}); setBranchRevenue(d.branchRevenue || []);
        setProductLeads(d.productLeads || []); setEmployeeLeads(d.employeeLeads || []);
      })
      .catch(err => setError(err.response?.data?.message || "Failed to load analytics. Please retry."))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { if (!filterOptionsLoading) fetchData(); }, [fetchData, filterOptionsLoading]);

  const busy = loading || filterOptionsLoading;
  const empBranches = [...new Set(employeeLeads.map(e => (e.branch || "").trim().toUpperCase()).filter(Boolean))];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        body { font-family: 'Inter', sans-serif; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        .stats-scrollbar::-webkit-scrollbar       { height: 5px; }
        .stats-scrollbar::-webkit-scrollbar-track { background: rgba(15,23,42,0.04); border-radius: 10px; }
        .stats-scrollbar::-webkit-scrollbar-thumb { background: rgba(15,23,42,0.18); border-radius: 10px; }
        .stats-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(15,23,42,0.32); }

        .charts-2col {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 20px;
          align-items: start;
        }
        @media (max-width: 920px) {
          .charts-2col { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* PAGE WRAPPER — same #f0f4f8 as LeaveApplications */}
      <div className="min-w-full font-[Inter]" style={{ minHeight: "100vh", background: "#f0f4f8" }}>

        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onLogout={() => { localStorage.clear(); }} />

        <StatsHeader
          filters={filters}
          setFilters={setFilters}
          onRefresh={fetchData}
          loading={busy}
          filterOptions={filterOptions}
          onOpenSidebar={() => setSidebarOpen(true)}
        />

        <div className="relative z-10 w-full flex flex-col gap-5" style={{ padding: "20px 20px 40px" }}>

          {error && <ErrorBanner message={error} onRetry={fetchData} />}

          {/* Row 1 — Summary Tiles */}
          <section>
            <LeadSummaryCards summary={summary} loading={busy} />
          </section>

          {/* Row 2 — Revenue Pie | Product × Branch Bar */}
          <section className="charts-2col">
            {busy ? <SkeletonPie /> : <RevenuePieChart branchRevenue={branchRevenue} />}
            {busy ? <SkeletonCard /> : <ProductBranchBarChart productLeads={productLeads} />}
          </section>

          {/* Row 3 — Overall Products Bar | Employee Bar */}
          <section className="charts-2col">
            {busy ? <SkeletonCard /> : <OverallProductBarChart productLeads={productLeads} />}
            {busy ? <SkeletonCard /> : <EmployeeBranchBarChart employeeLeads={employeeLeads} branches={empBranches} />}
          </section>

        </div>
      </div>
    </>
  );
};

export default CompanyStatisticsPage;